import React, { useEffect, useMemo, useState } from 'react';
import { Button, Checkbox, Header, Icon, Input, Message } from 'react-frontier';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuthTokens, LoginAuth, SetLoading } from './AuthClasses';
// import Validator from '@payfront/components/Validator';
import AuthAPI, { CDN_URL } from './AuthAPI';
import './style/auth.scss';

var isEmail = (v: string)=>{
	return (/^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/gi).test(v);
}

var Login = ()=>{
	var { t } = useTranslation();
	var [params] = useSearchParams();
	var [activeLogin, setActiveLogin] = useState<LoginAuth>(null);
	var [loggedIn, setLoggedIn] = useState<boolean>(false);
	var [remember, setRemember] = useState<boolean>(false);
	var [email, setEmail] = useState<string>('');
	var [password, setPassword] = useState<string>('');
	var [errorPrompts, setErrorPrompts] = useState<string[]>(null);
	
	var continue_url = params.get('c');
	var valid_params = useMemo(()=>{
		if(!continue_url) return false;
		// var source = params.get('s');
		// if(!source) return false;
		return true;
	}, [params]);

	var getContinueUrl = (tokens: AuthTokens)=>{
		if(!continue_url || !tokens) return null;
		continue_url = continue_url.replace(/\/$/gi, '');
		var redirect_url = params.get('r');
		var continue_params : any = {
			a: tokens.access,
			e: tokens.expires,
		}
		if(redirect_url && redirect_url.length>=10){
			continue_params.r = redirect_url;
		}
		return `${continue_url}?${Object.keys(continue_params).map(a=>`${a}=${continue_params[a]}`).join('&')}`
	}

	useEffect(()=>{
		setActiveLogin(AuthAPI.getActiveLogin());
	}, []);

	var redirectContinue = (tokens: AuthTokens)=>{
		setTimeout(()=>{
			window.location.href = getContinueUrl(tokens);
		}, 2000);
	}

	var login = (setLoading: SetLoading)=>{
		var prompts : string[] = [];
		if(!email || !isEmail(email)){
			prompts.push(t('login.form.email'));
		}
		if(!password || password.length<6){
			prompts.push(t('login.form.password'));
		}

		setErrorPrompts(prompts);
		if(prompts.length>0) return;

		setLoading(true);
		AuthAPI.login(email, password, !!remember).then(res=>{
			if(res.error) return setErrorPrompts([res.message]);
			setLoggedIn(true);
			setActiveLogin(res.data);
			redirectContinue(res.data.tokens);
		}).catch(err=>{
			setErrorPrompts([t('common.unexpected_error', { code: 'LGN-1' })])
		}).finally(()=>{
			setLoading(false);
		})
	}

	var refreshLogin = (setLoading: SetLoading) : void=>{
		if(!activeLogin) return;
		setLoading(true);
		AuthAPI.refreshLogin(activeLogin.tokens.refresh).then(res=>{
			if(res.error) return setErrorPrompts([res.message]);
			setLoggedIn(true);
			setActiveLogin(res.data);
			redirectContinue(res.data.tokens);
		}).catch(err=>{
			setErrorPrompts([t('common.unexpected_error', { code: 'LGN-2' })])
		}).finally(()=>{
			setLoading(false);
		})
	}

	var logout = ()=>{
		AuthAPI.logout();
		setActiveLogin(null);
	}

	if(!valid_params){
		return <div className="pf auth">
			<div className="logo"></div>
			<div className="container">
				<Header size='big' text={'404'} subtext={t('login.invalid_config')} />
			</div>
		</div>
	}
	
	return <div className="pf auth">
		<div className="logo">
			<img src={`${CDN_URL}/logo/LogoH.webp`} />
		</div>
		{loggedIn ? (
			<div className="login completed">
				<Icon name='check-circle' solid />
				<Header text={t('login.hello', { name: activeLogin?.first_name })} subtext={t('login.redirected')} />
				{activeLogin ? (
					<Button text={t('common.continue')} as={Link} to={getContinueUrl(activeLogin.tokens)} />
				) : null}
			</div>
		) : activeLogin ? (
			<div className="login">
				<Icon name='user-circle' solid />
				<Header text={`${activeLogin.first_name} ${activeLogin.last_name}`} subtext={t('login.saved_login')} />
				<Message list={errorPrompts} type='error' />
				<Button text={t('login.form.button')} color='blue' onClick={refreshLogin} />
				<br />
				<Button text={t('login.change_account')} size='tiny' basic onClick={logout} />
			</div>
		) : (
			<div className="form">
				<Input label={t('login.form.email')} value={email} onChange={setEmail} />
				<Input label={t('login.form.password')} inputType='password' value={password} onChange={setPassword} />
				<Checkbox checked={remember} onChange={setRemember} label={t('login.form.remember')} style={{ marginTop: 15 }} />

				<Message type='error' list={errorPrompts} style={{ marginTop: 15 }} />

				<Button fluid style={{ marginTop: 15 }} color='blue' text={t('login.form.button')} onClick={login} />
				<div className="forgot">
					{t('login.no_account')}
					<Button size='tiny' basic color='blue' text={t('login.create_account')} as={Link} to={'/signup'} />
					<br />
					<Button text={t('login.forgot')} size='tiny' basic color='blue' as={Link} to={'/forgot'} />
				</div>
			</div>
		)}
	</div>
}

export default Login;